import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
// Components
import { LocationButton, RichText } from 'components/bos-modules';
import LegacyFooter from 'components/footers/LegacyFooter';
import { TOPImage } from 'components/global/Picture';
import LegacyLandingPageHeader from 'components/headers/LegacyLandingPageHeader';

// Constants
import { TOPLoggedError } from 'constants/Errors';

// Hooks
import { useStores } from 'hooks/use-stores';
import { Components } from 'stores/Types';

import {
    ComponentMap,
    FullHeightColumn,
    FullHeightRow,
    LandingPageImageContainer,
    LegacyDivider,
    LegacyPage,
} from './styles';
import { getLocationsInDropdown, onRedirect, onSubmit } from './utils';

function Legacy({ componentsToRender = [] }: { componentsToRender: Components[] }) {
  const history = useHistory();
  const {
    hostStore,
    uiState: { isMobile },
    locationStore,
  } = useStores();
  const { customerName } = locationStore; // A11Y: Used as page aria label

  const renderComponentsFromProps = Boolean(componentsToRender.length);
  const showLandingPageHeader = !renderComponentsFromProps;

  const landingPageImageSrc = isMobile
    ? hostStore?.assets.landing_page_mobile_image
    : hostStore?.assets.landing_page_desktop_image;

  const components = renderComponentsFromProps ? componentsToRender : hostStore.landingPageComponents;
  const locations = hostStore.landingPageLocations;
  const customersById = hostStore?.settings?.location_data?.customers_by_id;
  const cssVariables = hostStore?.theme?.css_variables;
  const align = cssVariables ? cssVariables['--content-align'] : 'left';

  let locationsToShowInDropdown = getLocationsInDropdown(locations, components);
  let locationsToShowInCateringDropdown = [];

  const externalUrlLocations = locations?.filter((location) => location.external_url);

  if (components?.some((component) => component.type === 'CateringLocationDropdown')) {
    locationsToShowInCateringDropdown = locationsToShowInDropdown.filter(
      (location) => location.fulfillment_method === 'catering' && location.locationShortId.includes('catering')
    );
    locationsToShowInDropdown = locationsToShowInDropdown.filter(
      (location) => !(location.fulfillment_method === 'catering' && location.locationShortId.includes('catering'))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const renderComponents = (componentsToRender: any[]) => {
    if (!componentsToRender) {
      return null;
    }
    if (typeof componentsToRender === 'string') {
      return componentsToRender;
    }
    if (!componentsToRender.length) {
      return null;
    }

    return (
      <>
        {components.map((component, i) => {
          const Cmp = ComponentMap[component.type];
          switch (component.type) {
            case 'ClaimPOSTabPage':
              return (
                <Cmp
                  key={i}
                  onClaimTab={component.props.onClaimTab}
                  unclaimedPosOnlyTabs={component.props.unclaimedPosOnlyTabs}
                  loading={component.props.loading}
                />
              );
            case 'LocationSearch':
              return (
                <Cmp
                  key={i}
                  onSearch={(locationCode: string) => onSubmit(locationCode, component.type, history)}
                  searchPlaceholderText={component.props.locationEntryPlaceholderText}
                  searchButtonText={component.props.orderButtonText}
                  style={component.props.style}
                  {...component.props}
                />
              );
            case 'AddressSearch':
              return (
                <Cmp
                  key={i}
                  onSearch={(locationCode: string) => onSubmit(locationCode, component.type, history)}
                  searchPlaceholderText={component.props.addressSearchPlaceholderText}
                  customersById={customersById}
                  locations={locations}
                  {...component.props}
                />
              );
            case 'LocationDropdown':
              return (
                <Cmp
                  key={i}
                  onSearch={(locationCode: string) => onSubmit(locationCode, component.type, history)}
                  dropdownText={component.props.locationDropdownPlaceholderText}
                  locations={locationsToShowInDropdown}
                />
              );
            case 'CateringLocationDropdown':
              return (
                <Cmp
                  key={i}
                  onSearch={(locationCode: string) => onSubmit(locationCode, component.type, history)}
                  dropdownText={component.props.locationDropdownPlaceholderText}
                  locations={locationsToShowInCateringDropdown}
                />
              );
            case 'LocationButtonGroup':
              return (
                <Cmp
                  key={i}
                  onSearch={(locationCode: string) => onSubmit(locationCode, component.type, history)}
                  locations={component.props.locations}
                />
              );
            case 'LocationButton':
              try {
                // We have to find the location via the locations available on the hostname so that we are able to get
                // the full location data including the landing page name of the location which we dont save to the template
                // because an owner can update the location code after publishing the template. If we got the data saved
                // to the template then that data would be stale.
                const locationData = locations.find(
                  (loc) =>
                    loc?.locationId === component?.props?.locationId ||
                    loc?.locationShortId === component?.props?.locationShortId
                );
                if (!locationData) {
                  throw new TOPLoggedError(
                    `You are trying to render a location button for a location code with name ${component?.props?.locationName} which is not available on this hostname. Please connect ${component?.props?.locationName} (id: ${component?.props?.locationId}, shortId: ${component?.props?.locationShortId}) to the hostame or update the published BOS template.`,
                    {
                      customer_id: hostStore?.host_customer?.customer_id,
                      endpoint: 'Legacy.js',
                    }
                  );
                }
                return (
                  <Cmp
                    key={i}
                    onSearch={(locationCode: string) => onSubmit(locationCode, component.type, history)}
                    {...locationData}
                  />
                );
              } catch (error) {
                return null;
              }
            case 'RichText': // must account for the case where there are external buttons and bottom rich text. We want to show the bottom rich text under the external buttons.
              if (i === components.length - 1 && externalUrlLocations.length > 0) {
                return null;
              } else {
                return (
                  <Cmp key={i} {...component.props}>
                    {renderComponents(component.children)}
                  </Cmp>
                );
              }
            case 'Divider':
              return <Cmp key={i} {...component.props} />;
            default:
              return (
                <Cmp key={i} {...component.props} style={component.props.style}>
                  {renderComponents(component.children)}
                </Cmp>
              );
          }
        })}
        {externalUrlLocations.length > 0 && <LegacyDivider dividerText="or" />}
        {externalUrlLocations?.map((externalLocation) => (
          <LocationButton key={externalLocation.id} onSearch={onRedirect} {...externalLocation} />
        ))}

        {/* If there is bottom rich text and there are external buttons, show the bottom rich text under the external buttons. */}
        {components[components.length - 1].type === 'RichText' && externalUrlLocations.length > 0 && (
          <RichText {...components[components.length - 1].props}>
            {renderComponents(components[components.length - 1].children)}
          </RichText>
        )}
      </>
    );
  };

  return (
    <LegacyPage
      style={{ position: 'relative' }}
      showHeader={showLandingPageHeader}
      HeaderComponent={<LegacyLandingPageHeader />}
      FooterComponent={<LegacyFooter />}
      title="Home"
      aria-label={customerName}
    >
      <FullHeightRow>
        <FullHeightColumn centered={align === 'center'}>{renderComponents(components)}</FullHeightColumn>
      </FullHeightRow>

      <LandingPageImageContainer data-has-image={landingPageImageSrc ? 1 : 0}>
        {landingPageImageSrc && <TOPImage src={landingPageImageSrc} alt="" />}
      </LandingPageImageContainer>
    </LegacyPage>
  );
}

export default observer(Legacy);
